import { ClassroomDiagnosticTask } from './ClassroomDiagnosticTask'
import type { DiagnosticStudentResultEntity } from '@/core/infrastructure/schemas/dashboard/ClassroomDiagnosticResultsSchema'

/**
 * Classe représentant les résultats de diagnostic d'un étudiant
 */
export class ClassroomDiagnosticStudentResult {
  readonly identifier: string
  readonly firstname: string
  readonly lastname: string
  readonly periodId: number
  readonly schoolYearName: string
  readonly classroomId: number
  readonly groupId: number | null
  readonly groupName: string | null
  readonly teacherId: number | null
  readonly teacherFirstname: string | null
  readonly teacherLastname: string | null
  readonly tasks: ClassroomDiagnosticTask[]

  constructor(data: DiagnosticStudentResultEntity) {
    this.identifier = data.identifier
    this.firstname = data.firstname
    this.lastname = data.lastname
    this.periodId = data.periodId
    this.schoolYearName = data.schoolYearName
    this.classroomId = data.classroomId
    this.groupId = data.groupId
    this.groupName = data.groupName
    this.teacherId = data.teacherId
    this.teacherFirstname = data.teacherFirstname
    this.teacherLastname = data.teacherLastname
    this.tasks = data.tasks.map((task) => ClassroomDiagnosticTask.create(task))
  }

  /**
   * Obtient le nom complet de l'étudiant
   */
  public getFullName(): string {
    return `${this.firstname} ${this.lastname}`
  }

  /**
   * Obtient le nom complet de l'enseignant
   */
  public getTeacherFullName(): string | null {
    if (!this.teacherFirstname || !this.teacherLastname) return null
    return `${this.teacherFirstname} ${this.teacherLastname}`
  }

  /**
   * Obtient les résultats pour une tâche spécifique
   */
  public getTaskResult(taskId: number): ClassroomDiagnosticTask | undefined {
    return this.tasks.find((task) => task.taskId === taskId)
  }

  /**
   * Vérifie si l'étudiant a commencé au moins un diagnostic
   */
  public hasStartedAnyDiagnostic(): boolean {
    return this.tasks.some((task) => task.hasStartedDiagnostic())
  }

  /**
   * Vérifie si l'étudiant a terminé au moins un diagnostic de fin
   */
  public hasCompletedAnyEndDiagnostic(): boolean {
    return this.tasks.some((task) => task.hasCompletedEndDiagnostic())
  }

  /**
   * Obtient la moyenne des scores de diagnostic début
   */
  public getAverageDiagStartScore(): number | null {
    const tasksWithStartScore = this.tasks.filter((task) => task.hasStartedDiagnostic())
    if (tasksWithStartScore.length === 0) return null

    const total = tasksWithStartScore.reduce(
      (sum, task) => sum + (task.diagScorePercentRounded || 0),
      0
    )
    return Math.round(total / tasksWithStartScore.length)
  }

  /**
   * Obtient la moyenne des scores de diagnostic fin
   */
  public getAverageDiagEndScore(): number | null {
    const tasksWithEndScore = this.tasks.filter((task) => task.hasCompletedEndDiagnostic())
    if (tasksWithEndScore.length === 0) return null

    const total = tasksWithEndScore.reduce(
      (sum, task) => sum + (task.diagEndScorePercentRounded || 0),
      0
    )
    return Math.round(total / tasksWithEndScore.length)
  }

  /**
   * Obtient la progression moyenne entre diagnostic début et fin
   */
  public getAverageDiagnosticProgression(): number | null {
    const tasksWithProgression = this.tasks
      .map((task) => task.getDiagnosticProgression())
      .filter((progression) => progression !== null) as number[]

    if (tasksWithProgression.length === 0) return null

    const total = tasksWithProgression.reduce((sum, progression) => sum + progression, 0)
    return Math.round(total / tasksWithProgression.length)
  }

  /**
   * Crée une instance à partir de données brutes
   */
  public static create(data: unknown): ClassroomDiagnosticStudentResult {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid data for ClassroomDiagnosticStudentResult')
    }

    const studentData = data as DiagnosticStudentResultEntity
    return new ClassroomDiagnosticStudentResult(studentData)
  }

  /**
   * Crée plusieurs instances à partir d'un tableau de données
   */
  public static createMany(dataArray: unknown[]): ClassroomDiagnosticStudentResult[] {
    if (!Array.isArray(dataArray)) {
      throw new Error('Expected array for ClassroomDiagnosticStudentResult.createMany')
    }

    return dataArray.map((data) => ClassroomDiagnosticStudentResult.create(data))
  }
}
